import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import image1 from '../assets/riyal.png'
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { api } from '../utils/apiService';
import ExportButton from '../components/ExportButton';
import Loader from '../components/Loader';
import { format } from 'date-fns';
import { useTranslation } from '../hooks/useTranslation';

// Module-level cache to preserve pagination state across navigation
let cachedPage = 0;
let cachedRowsPerPage = 10;
let cachedSearchQuery = '';

function Quotation() {
  const { t } = useTranslation();
  const [quotations, setQuotations] = useState([]);
  const [searchQuery, setSearchQuery] = useState(cachedSearchQuery);
  const [page, setPage] = useState(cachedPage);
  const [rowsPerPage, setRowsPerPage] = useState(cachedRowsPerPage);
  const [totalQuotations, setTotalQuotations] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    status: {
      draft: true,
      approved: true,
      po: true,
      rejected: true,
      sent: true,
      partial: true,
      expired: true,
    }
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quotationToDelete, setQuotationToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState(null);
  const [selectedQuotationForStatus, setSelectedQuotationForStatus] = useState(null);

  // Define export columns
  const exportColumns = [
    { field: 'quotationNo', headerName: 'Quotation #', width: 150 },
    { field: 'customerName', headerName: 'Customer', width: 200 },
    { field: 'quotationDate', headerName: 'Date', width: 120 },
    { field: 'validUntil', headerName: 'Valid Until', width: 120 },
    { field: 'totalAmt', headerName: 'Amount', width: 120 },
    { field: 'status', headerName: 'Status', width: 120 }
  ];

  // Format data for export
  const getExportData = () => {
    return filteredQuotations.map(quotation => ({
      quotationNo: quotation.quotationNo || '',
      customerName: quotation.customerName || 'Walk In Customer',
      quotationDate: quotation.quotationDate ? format(new Date(quotation.quotationDate), 'dd/MM/yyyy') : '',
      validUntil: quotation.validUntil ? format(new Date(quotation.validUntil), 'dd/MM/yyyy') : '',
      totalAmt: quotation.totalAmt ? Number(quotation.totalAmt).toFixed(2) : '0.00',
      status: quotation.status || ''
    }));
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    cachedSearchQuery = value;
    setPage(0);
    cachedPage = 0;
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        ...value,
      },
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    cachedPage = newPage;
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    cachedRowsPerPage = newRowsPerPage;
    setPage(0);
    cachedPage = 0;
  };

  const handleEditClick = (quotationNo) => {
    navigate(`/quotation/edit/${quotationNo}`);
  };

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await api.get('/quotations', { params });
      
      const responseData = response?.data;
      
      let data = [];
      let total = 0;
      
      if (responseData?.success !== undefined) {
         data = responseData.data || [];
         total = responseData.pagination?.total || responseData.total || (Array.isArray(data) ? data.length : 0);
      } else if (responseData?.data) {
         data = responseData.data;
         total = responseData.pagination?.total || responseData.total || (Array.isArray(data) ? data.length : 0);
      } else if (Array.isArray(responseData)) {
         data = responseData;
         total = responseData.length;
      } else if (Array.isArray(response)) {
         data = response;
         total = response.length;
      }

      setQuotations(Array.isArray(data) ? data : []);
      setTotalQuotations(total);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setNotification({
        open: true,
        message: 'Error fetching quotations: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async (quotationNo) => {
    try {
      setDeleting(true);
      await api.delete(`/quotations/${quotationNo}`);
      setNotification({
        open: true,
        message: 'Quotation deleted successfully',
        severity: 'success'
      });
      setDeleteDialogOpen(false);
      fetchQuotations();
    } catch (error) {
      console.error('Error deleting quotation:', error);
      setNotification({
        open: true,
        message: 'Error deleting quotation: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleConvertToInvoice = async (quotation) => {
    try {
      // Call the convert-to-invoice endpoint
      const response = await api.get(`/quotations/${quotation.quotationNo}/convert-to-invoice`);
      if (response.data && response.data.success) {
        const invoiceData = response.data.data;

        // Navigate to sales invoice page with converted invoice data
        navigate('/sales/new-invoice', {
          state: {
            fromQuotation: true,
            quotationNo: quotation.quotationNo,
            invoiceData: invoiceData
          }
        });
      }
    } catch (error) {
      console.error('Error converting quotation to invoice:', error);
      setNotification({
        open: true,
        message: 'Error converting quotation: ' + (error.response?.data?.message || error.message || 'Unknown error'),
        severity: 'error'
      });
    }
  };

  const handlePrintClick = async (quotation) => {
    if (!quotation) return;

    try {
      setLoading(true);
      const quotationNo = quotation.quotationNo;
      const type = quotation.customerType === 'B2B' ? 'a4' : 'thermal';

      const response = await api.get(`/print/quotation/${quotationNo}?type=a4`);
      const { html, css } = response.data;

      const printWindow = window.open('', '_blank');
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Quotation ${quotationNo}</title>
            <meta charset="utf-8">
            <style>
              ${css}
            </style>
          </head>
          <body>
            ${html}
            <script>
              window.onload = function() {
                window.print();
                window.close();
              }
            </script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      setNotification({
        open: true,
        message: 'Print window opened successfully',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        open: true,
        message: `Print failed: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = (event, quotation) => {
    setStatusMenuAnchorEl(event.currentTarget);
    setSelectedQuotationForStatus(quotation);
  };

  const handleStatusClose = () => {
    setStatusMenuAnchorEl(null);
    setSelectedQuotationForStatus(null);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedQuotationForStatus) return;

    // Prevent changing to the same status
    if (selectedQuotationForStatus.status === newStatus) {
      handleStatusClose();
      return;
    }

    try {
      setLoading(true);
      await api.patch(`/quotations/${selectedQuotationForStatus.quotationNo}/status`, { status: newStatus });

      setNotification({
        open: true,
        message: 'Status updated successfully',
        severity: 'success'
      });
      fetchQuotations();
    } catch (error) {
      console.error('Error updating status:', error);
      setNotification({
        open: true,
        message: 'Error updating status: ' + (error.response?.data?.message || error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
      handleStatusClose();
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchQuotations();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchQuery]);

  const filteredQuotations = Array.isArray(quotations) ? quotations.filter(quotation => {
    // Status filter
    const statusMatch =
      (filters.status.draft && quotation.status === 'draft') ||
      (filters.status.approved && quotation.status === 'approved') ||
      (filters.status.po && quotation.status === 'po') ||
      (filters.status.rejected && quotation.status === 'rejected') ||
      (filters.status.sent && quotation.status === 'sent') ||
      (filters.status.partial && quotation.status === 'partial') ||
      (filters.status.expired && quotation.status === 'expired');

    return statusMatch;
  }) : [];

  const paginatedQuotations = quotations.length > rowsPerPage
    ? filteredQuotations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : filteredQuotations;

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
          {t('Quotations.Quotations')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t('Quotations.Manageyourquotations')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <ExportButton
            columns={exportColumns}
            data={getExportData()}
            filename="quotations"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => navigate('/quotation/new')}
            sx={{
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            {t('Quotations.New_Quotations')}
          </Button>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
            py: 0.5,
            minHeight: 36,
          }
        }}
      >
        <Tab label={t('Quotations.Quotations')} />
      </Tabs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder={t('Quotations.Search_quotations')}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '100%',
            maxWidth: 500,
            height: 28,
            '& .MuiOutlinedInput-root': {
              borderRadius: '2px',
              height: 28,
            },
            '& .MuiInputBase-input': {
              py: 0.5,
            }
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          size="small"
          onClick={handleFilterClick}
          sx={{
            borderRadius: '2px',
            textTransform: 'none',
            py: 0.25,
            px: 1,
            height: 28,
            ml: 1,
          }}
        >
         {t('Common.Filter')}
        </Button>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: {
              width: 200,
              p: 1,
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>Status</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.draft}
                  onChange={(e) => handleFilterChange('status', { draft: e.target.checked })}
                  size="small"
                />
              }
              label="Draft"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.approved}
                  onChange={(e) => handleFilterChange('status', { approved: e.target.checked })}
                  size="small"
                />
              }
              label="Approved"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.po}
                  onChange={(e) => handleFilterChange('status', { po: e.target.checked })}
                  size="small"
                />
              }
              label="PO"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.rejected}
                  onChange={(e) => handleFilterChange('status', { rejected: e.target.checked })}
                  size="small"
                />
              }
              label="Rejected"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.sent}
                  onChange={(e) => handleFilterChange('status', { sent: e.target.checked })}
                  size="small"
                />
              }
              label="Sent"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.partial}
                  onChange={(e) => handleFilterChange('status', { partial: e.target.checked })}
                  size="small"
                />
              }
              label="Partial"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.expired}
                  onChange={(e) => handleFilterChange('status', { expired: e.target.checked })}
                  size="small"
                />
              }
              label="Expired"
            />
          </FormGroup>
        </Menu>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          mb: 0.5,
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: '4px',
        }}
      >
        <Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '120px' }}>{t('Quotations.Quotation')} #</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>{t('Sales.Date')}</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>{t('Sales.Customer')}</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>{t('Quotations.Valid_Until')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>{t('Common.Subtotal')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>{t('common.VAT_Amount')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>{t('Common.Total_Amount')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>{t('Common.Status')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '120px' }}>{t('Common.Actions')}</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading && paginatedQuotations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Loader
                        loading={true}
                        size="medium"
                        text="Loading quotations..."
                      />
                    </TableCell>
                  </TableRow>
                ) : paginatedQuotations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No quotations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedQuotations.map((quotation) => (
                    <TableRow
                      key={quotation._id}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="left" sx={{ py: 0.5 }}>
                        <Typography variant="body2" color="primary" fontWeight="medium">
                          {quotation.quotationNo}
                        </Typography>
                      </TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>
                        {quotation.quotationDate ? new Date(quotation.quotationDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>
                        {quotation.customerName || 'Walk In Customer'}
                      </TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>
                        {quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}><img src={image1} alt="Currency" style={{ width: '13px', height: '13px', marginRight: '4px' }} />{quotation.subTotal?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}><img src={image1} alt="Currency" style={{ width: '13px', height: '13px', marginRight: '4px' }} />{quotation.vatAmt?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}><img src={image1} alt="Currency" style={{ width: '13px', height: '13px', marginRight: '4px' }} />{quotation.totalAmt?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Chip
                          label={quotation.status === 'po' ? 'PO' : quotation.status || 'draft'}
                          size="small"
                          color={
                            quotation.status === 'approved' ? 'success' :
                              quotation.status === 'po' ? 'secondary' :
                                quotation.status === 'rejected' ? 'error' :
                                  quotation.status === 'sent' ? 'info' :
                                    quotation.status === 'partial' ? 'warning' :
                                      'default'
                          }
                          variant="outlined"
                          onClick={(e) => handleStatusClick(e, quotation)}
                          sx={{
                            fontWeight: 'medium',
                            textTransform: quotation.status === 'po' ? 'uppercase' : 'capitalize',
                            cursor: 'pointer',
                            backgroundColor:
                              quotation.status === 'approved' ? 'rgba(46, 205, 111, 0.1)' :
                                quotation.status === 'po' ? 'rgba(156, 39, 176, 0.1)' :
                                  quotation.status === 'rejected' ? 'rgba(244, 67, 54, 0.1)' :
                                    quotation.status === 'sent' ? 'rgba(33, 150, 243, 0.1)' :
                                      quotation.status === 'partial' ? 'rgba(255, 152, 0, 0.1)' :
                                        quotation.status === 'expired' ? 'rgba(158, 158, 158, 0.1)' :
                                          'rgba(0, 0, 0, 0.08)'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          {quotation.convertedToInvoice ? (
                            <Chip
                              label={`Converted (${quotation.invoiceNo || 'Invoice'})`}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{
                                fontWeight: 'medium',
                                fontSize: '0.75rem',
                                height: '24px'
                              }}
                            />
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                color="success"
                                sx={{ mx: 0.25, p: 0.5 }}
                                onClick={() => handleConvertToInvoice(quotation)}
                                title="Convert to Invoice"
                              >
                                <ReceiptIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="primary"
                                sx={{ mx: 0.25, p: 0.5 }}
                                onClick={() => handleEditClick(quotation.quotationNo)}
                                title="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={{ mx: 0.25, p: 0.5, color: 'text.secondary' }}
                                onClick={() => handlePrintClick(quotation)}
                                title="Print"
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                sx={{ mx: 0.25, p: 0.5 }}
                                onClick={() => {
                                  setQuotationToDelete(quotation);
                                  setDeleteDialogOpen(true);
                                }}
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalQuotations > 0 ? totalQuotations : filteredQuotations.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              '& .MuiTablePagination-select': {
                py: 0.5,
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Quotation?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the quotation {quotationToDelete?.quotationNo}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Loader
            loading={deleting}
            size="small"
            text="Deleting..."
            position="left"
            buttonProps={{
              color: "error",
              variant: "contained",
              onClick: () => {
                handleDeleteClick(quotationToDelete?.quotationNo);
              }
            }}
          >
            Delete
          </Loader>
        </DialogActions>
      </Dialog>


      <Menu
        anchorEl={statusMenuAnchorEl}
        open={Boolean(statusMenuAnchorEl)}
        onClose={handleStatusClose}
        PaperProps={{
          sx: {
            width: 150,
          },
        }}
      >
        <MenuItem
          onClick={() => handleStatusChange('draft')}
          selected={selectedQuotationForStatus?.status === 'draft'}
        >
          Draft
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange('approved')}
          selected={selectedQuotationForStatus?.status === 'approved'}
        >
          Approved
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange('po')}
          selected={selectedQuotationForStatus?.status === 'po'}
        >
          PO
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange('rejected')}
          selected={selectedQuotationForStatus?.status === 'rejected'}
        >
          Rejected
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange('sent')}
          selected={selectedQuotationForStatus?.status === 'sent'}
        >
          Sent
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange('partial')}
          selected={selectedQuotationForStatus?.status === 'partial'}
        >
          Partial
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange('expired')}
          selected={selectedQuotationForStatus?.status === 'expired'}
        >
          Expired
        </MenuItem>
      </Menu>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Quotation;
